import React from 'react';
import _ from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from 'ckeditor5-build-full';
import {Button, Checkbox, Input, Message, Navigate, Select} from '../components';
import {__, formatAmount, getCurrency, getCurrencyPrice, parseJson, request, toNumber} from '../functions';
import '../assets/styles/product-marketplace.css';
import {Autocomplete, ButtonBase, Chip, Tab, Tabs, TextField, Tooltip} from "@mui/material";
import {IMAGES, MARKETPLACES, MARKETPLACES_COLORS, AI_TRANSLATE_FUNCTION_SLUG} from "../config";
import ActivatedIcon from "@mui/icons-material/CheckCircle";
import { convertDescriptionToItems } from '@allegro/convert-description';
import SmartToyIcon from "@mui/icons-material/SmartToy";

/**
 * Produkt komponenta.
 */
class ProductMarketplace extends Navigate {
    static defaultProps = {
        id: 0,
        user: {},
    };

    state = {
        marketplace_id: '',
        marketplace: {},
        data: {},
        settings: {},
        loading: false,
        loadingMarketplace: false,
        loadingCreate: false,
        loadingRemove: false,
        create_error: '',
        marketplace_product_id: '',
        loadingAllegroParameters: false,
        allegro_categories_query: '',
        allegro_categories_searched: [],
        allegro_parameters: [],
        allegro_parameter_types: {},
        allegro_parameter_translations: {},
        allegro_market: 'pl',
        allegro_main_market: 'pl',
        allegro_invalid_category: false,
        allegro_ai_pair_parameters_loading: false,
        allegro_ai_pair_parameters_price: '',
        allegro_ai_pair_parameters_error: '',
        allegro_ai_translate_loading: false,
        lightbox: {
            remove: false,
        },
    };

    marketplaceTimeout = null;
    allegro_categories_search_timeout = null;

    componentDidMount() {
        this.fetch();
    }

    fetch() {
        const { id, onClose, setTitle, showSnackbar } = this.props;

        request(`/products-full/marketplaces/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                // Produkt neexistuje
                showSnackbar('error', __('Produkt neexistuje'));
                onClose();
                return;
            }

            if (!_.isEmpty(data.product.translations)) {
                // Nastavime title
                setTitle(_.truncate(data.product.translations[0].name, { length: this.isMobile() ? 32 : 64 }));
            }

            let marketplace_id = toNumber(_.keys(data.marketplaces)[1]);

            if (!_.isEmpty(data.user_marketplaces)) {
                marketplace_id = toNumber(data.user_marketplaces[0]);
            }

            this.setState({
                data,
                marketplace_id,
                marketplace: data.marketplace,
                ...this.getMarketplaceSettings(data, data.marketplaces[marketplace_id], data.marketplace)
            });
        });
    }

    onChangeSetting(name, value, additional = {}) {
        const { settings } = this.state;

        this.setState({ settings: { ...settings, [name]: value }, ...additional });
    }

    renderValue(value) {
        return !_.isEmpty(value) ? value : '-';
    }

    showHtmlEditor(value) {
        return value.toString().indexOf('</p>') !== -1 || value.toString().indexOf('</ul>') !== -1;
    }

    fetchMarketplace(marketplace_id) {
        const { id, showSnackbar } = this.props;

        request(`/products-full/marketplace/${id}/${marketplace_id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.setState({ loadingMarketplace: false });
                showSnackbar('error', __('Trhovisko sa nepodarilo načítať'));
                return;
            }

            this.setState({
                marketplace: data.marketplace,
                loadingMarketplace: false,
                ...this.getMarketplaceSettings(this.state.data, this.state.data.marketplaces[marketplace_id], data.marketplace)
            });
        });
    }

    getMarketplaceSettings(data, marketplace, marketplace_data) {
        switch (marketplace) {
            case 'Allegro':
                if (!_.isEmpty(marketplace_data)) {
                    if (marketplace_data.data.category_id !== '') {
                        setTimeout(() => this.selectAllegroCategory(marketplace_data.data.category_id), 500);
                    }

                    let allegro_market = marketplace_data.data.base_marketplace;
                    let markets_settings = marketplace_data.data.markets_settings;

                    if (
                        allegro_market === ''
                        && !_.isEmpty(data.product.translations)
                        && _.includes(marketplace_data.data.markets, data.product.translations[0].language)
                    ) {
                        allegro_market = data.product.translations[0].language;
                    }

                    markets_settings = { ...markets_settings, [allegro_market]: { ...markets_settings[allegro_market], active: true } };

                    return {
                        settings: {
                            product_id: marketplace_data.data.product_id,
                            category_id: marketplace_data.data.category_id,
                            parameters: {},
                            parameter_types: {},
                            markets: markets_settings,
                            stock: data.product.stock,
                            market: allegro_market,
                        },
                        allegro_market,
                        allegro_main_market: allegro_market,
                        marketplace_product_id: !_.isEmpty(marketplace_data.product) ? marketplace_data.product.id : '',
                    };
                }
                break;
        }

        return { settings: {} };
    }

    changeMarketplace(marketplace_id) {
        this.setState({ marketplace_id, loadingMarketplace: true });

        if (this.marketplaceTimeout !== null) {
            clearTimeout(this.marketplaceTimeout);
        }

        this.marketplaceTimeout = setTimeout(() => this.fetchMarketplace(marketplace_id), 500);
    }

    onChangeAllegroParameter(id, value) {
        const { settings } = this.state;

        this.onChangeSetting('parameters', { ...settings.parameters, [id]: value });
    }

    allegroSearchCategories(query) {
        if (this.allegro_categories_search_timeout !== null) {
            clearTimeout(this.allegro_categories_search_timeout);
        }

        if (query.length >= 3) {
            this.allegro_categories_search_timeout = setTimeout(() => this.runAllegroSearchCategories(query), 500);
        }

        this.setState({ allegro_categories_query: query });
    }

    runAllegroSearchCategories(query) {
        request(`/products-full/allegroCategories/${query}`).then(response => {
            const { status, data } = response.data;

            this.setState({ allegro_categories_searched: status === 'success' ? data.categories : [] });
        });
    }

    onChangeAllegroMarket(allegro_market) {
        this.setState({ allegro_market });
    }

    onChangeAllegroMarketSetting(name, value) {
        const { allegro_market, settings } = this.state;

        this.onChangeSetting('markets', { ...settings.markets, [allegro_market]: { ...settings.markets[allegro_market], [name]: value } });
    }

    isRequiredAllegroParameter(parameter, settings) {
        return (_.has(parameter, 'required') && parameter.required)
            || (_.has(parameter, 'requiredForProduct') && parameter.requiredForProduct && settings.product_id === '');
    }

    selectAllegroCategory(value) {
        const { settings } = this.state;

        this.onChangeSetting('category_id', value, { loadingAllegroParameters: true });

        request(`/products-full/allegroParameters/${value}`).then(response => {
            const { status, data } = response.data;

            const parameters = status === 'success' ? data.parameters : [];
            const parametersSettings = _.reduce(parameters, (result, parameter) => {
                const is_required = this.isRequiredAllegroParameter(parameter, settings);

                if (!is_required) {
                    return result;
                }

                let value = '';

                switch (parameter.name) {
                    case 'Marka':
                        const manufacturers = _.has(parameter, 'dictionary') ? parameter.dictionary : [];
                        const product_name = !_.isEmpty(this.state.data.product.translations) ? this.state.data.product.translations[0].name.toLowerCase() : '';
                        const product_manufacturer = _.has(this.state.data.product, 'manufacturer') ? this.state.data.product.manufacturer.toLowerCase() : '';

                        let valueByManufacturer = '';
                        let valueByName = '';
                        let longestValueName = 0;

                        _.each(manufacturers, manufacturer => {
                            const manufacturer_value = manufacturer.value.toLowerCase();

                            if (product_manufacturer !== '' && manufacturer_value === product_manufacturer) {
                                valueByManufacturer = manufacturer.id;
                            }

                            const index = product_name !== '' ? product_name.indexOf(manufacturer_value) : -1;

                            if (index !== -1) {
                                let length = manufacturer_value.length;

                                if (index === 0) {
                                    // Priority
                                    length += 5;
                                }

                                if (length > longestValueName) {
                                    longestValueName = length;
                                    valueByName = manufacturer.id;
                                }
                            }
                        });

                        value = valueByManufacturer !== '' ? valueByManufacturer : valueByName;
                        break;

                    case 'EAN (GTIN)':
                        const product_ean = this.state.data.product.ean;

                        if (product_ean !== '') {
                            value = product_ean;
                        }
                        break;

                    case 'Kod producenta':
                        const product_number = this.state.data.product.number;

                        if (product_number !== '') {
                            value = product_number;
                        }
                        break;

                    default:
                        break;
                }

                return { ...result, [parameter.id]: value };
            }, {});

            const parameter_types = _.reduce(parameters, (result, parameter) => {
                return { ...result, [parameter.id]: parameter.type };
            }, {});

            let allegro_invalid_category = false;

            if (!_.isEmpty(data.category) && _.has(data.category, 'options')) {
                if (settings.product_id === '' && !data.category.options.productCreationEnabled) {
                    allegro_invalid_category = true;
                } else if (!data.category.options.offersWithProductPublicationEnabled) {
                    allegro_invalid_category = true;
                }
            }

            if (allegro_invalid_category) {
                this.onChangeSetting('category_id', '', {
                    loadingAllegroParameters: false,
                    allegro_parameters: [],
                    allegro_parameter_types: {},
                    allegro_invalid_category: true,
                });
            } else {
                this.onChangeSetting('parameters', parametersSettings, {
                    loadingAllegroParameters: false,
                    allegro_parameters: parameters,
                    allegro_parameter_types: parameter_types,
                    allegro_parameter_translations: data.translations,
                    allegro_invalid_category: false,
                });
            }
        });
    }

    getCreateError(type, settings) {
        let error = '';

        switch (type) {
            case 'allegro':
                let valid_parameters = true;

                _.each(settings.parameters, value => {
                    if (value === '') {
                        valid_parameters = false;
                    }
                });

                if (!valid_parameters) {
                    error = __('Niektorý z parametrov nie je vyplnený');
                } else {
                    _.each(settings.markets, (market, country) => {
                        if (market.active) {
                            if (market.name === '') {
                                error = __('Názov pre {COUNTRY} lokalizáciu nie je vyplnený').replace('{COUNTRY}', country.toUpperCase());
                            } else if (market.description === '') {
                                error = __('Popis pre {COUNTRY} lokalizáciu nie je vyplnený').replace('{COUNTRY}', country.toUpperCase());
                            } else if (market.price === '') {
                                error = __('Cena pre {COUNTRY} lokalizáciu nie je vyplnená').replace('{COUNTRY}', country.toUpperCase());
                            }
                        }
                    });
                }

                break;
        }

        return error;
    }

    create(type) {
        const { id, showSnackbar } = this.props;
        let { settings, marketplace_id, allegro_parameter_types } = this.state;

        const error = this.getCreateError(type, settings);

        if (error !== '') {
            this.showSnackbar('error', error);
            return;
        }

        switch (type) {
            case 'allegro':
                const markets = _.reduce(settings.markets, (result, market_data, market) => {
                    if (market_data.description !== '') {
                        market_data = { ...market_data, description: convertDescriptionToItems(market_data.description) };
                    }

                    return { ...result, [market]: market_data };
                }, {});

                settings = { ...settings, parameter_types: allegro_parameter_types, markets };
                break;
        }

        this.setState({ loadingCreate: true });

        request(`/products-full/create-on-marketplace/${id}/${marketplace_id}`, { settings }, 'POST').then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.setState({ loadingCreate: false, create_error: `${__('Chyba')}: ${data.message}` });
                showSnackbar('error', __('Produkt sa nepodarilo vytvoriť'));
                return;
            }

            this.setState({
                loadingCreate: false,
                marketplace: {},
                data: {},
                settings: {},
                create_error: '',
                marketplace_product_id: '',
                allegro_parameters: [],
                allegro_ai_pair_parameters_price: '',
                allegro_ai_pair_parameters_error: '',
            });
            this.fetch();
        });
    }

    confirmRemove(id) {
        this.showLightbox('remove', { id });
    }

    remove() {
        const { showSnackbar } = this.props;
        const { marketplace_id, lightbox } = this.state;

        this.closeLightbox('remove');

        this.setState({ loadingRemove: true });

        request(`/products-full/remove-from-marketplace/${lightbox.remove.id}/${marketplace_id}`).then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.setState({ loadingRemove: false });
                showSnackbar('error', __('Produkt sa nepodarilo zmazať'));
                return;
            }

            this.setState({
                loadingRemove: false,
                marketplace_product_id: '',
                marketplace: {},
                data: {},
                settings: {},
                allegro_parameters: [],
            });
            this.fetch();
        });
    }

    pairAllegroParameters() {
        const { data, settings, allegro_parameters, allegro_parameter_translations } = this.state;

        if (_.isEmpty(data.product.translations)) {
            this.showSnackbar('error', __('Nie sú dostupné dáta o produkte na zaradenie parametrov'));
            return;
        }

        let parameters_last_options = {};
        let parameters_options = {};

        const parameters = _.reduce(allegro_parameters, (result, parameter) => {
            if (
                !this.isRequiredAllegroParameter(parameter, settings)
                || (_.has(settings.parameters, parameter.id) && settings.parameters[parameter.id] !== '')
            ) {
                return result;
            }

            let additional_parameter = {};

            switch (parameter.type) {
                case 'dictionary':
                    const options = _.has(parameter, 'dictionary') ? parameter.dictionary : [];

                    if (!_.isEmpty(options) && options.length > 0) {
                        parameters_last_options = { ...parameters_last_options, [parameter.id]: options[options.length - 1].id };
                    }

                    const formatted_options = _.reduce(options, (result, option) => {
                        return { ...result, [option.id]: _.has(allegro_parameter_translations, option.value)
                                ? `${allegro_parameter_translations[option.value]} / ${option.value}`
                                : option.value };
                    }, {});

                    parameters_options = { ...parameters_options, [parameter.id]: _.keys(formatted_options) };

                    additional_parameter = {
                        options: formatted_options,
                    };
                    break;

                case 'integer':
                case 'float':
                case 'string':
                    additional_parameter = {
                        min_length: _.has(parameter.restrictions, 'minLength') ? toNumber(parameter.restrictions.minLength) : 0,
                        max_length: _.has(parameter.restrictions, 'maxLength') ? toNumber(parameter.restrictions.maxLength) : 64,
                    };
                    break;
            }

            let parameter_formatted = {
                id: parameter.id,
                name: _.has(allegro_parameter_translations, parameter.name) ? allegro_parameter_translations[parameter.name] : parameter.name,
                type: parameter.type === 'dictionary' ? 'select' : 'text',
                ...additional_parameter
            };

            return [ ...result, parameter_formatted ];
        }, []);

        if (_.isEmpty(parameters)) {
            this.showSnackbar('error', __('Všetky parametre sú už zaradené'));
            return;
        }

        this.setState({ allegro_ai_pair_parameters_loading: true });

        const main_translation = data.product.translations[0];

        const input = {
            product: {
                translations: [{
                    language: main_translation.language,
                    name: _.has(main_translation, 'name') ? main_translation.name : '',
                    description: _.has(main_translation, 'description') ? main_translation.description : '',
                }],
                parameters: _.has(data.product, 'parameters') ? data.product.parameters : [],
            },
            parameters,
        };

        request('/ai-functions/run/PairAllegroParameter', {
            input: JSON.stringify(input),
            item_type: 'manual',
            item_id: null,
            variables: {},
        }, 'POST').then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                let error = data.error;

                if (error === 'Empty credit') {
                    error = __('Nemáte dostatočný kredit');
                }

                this.setState({
                    allegro_ai_pair_parameters_loading: false,
                    allegro_ai_pair_parameters_price: '',
                    allegro_ai_pair_parameters_error: `${__('Chyba')}: ${error}`,
                });
                return;
            }

            this.setState({
                allegro_ai_pair_parameters_loading: false,
                allegro_ai_pair_parameters_price: data.price,
                allegro_ai_pair_parameters_error: '',
            });

            const items = parseJson(data.output);
            const new_parameters = _.reduce(settings.parameters, (result, value, id) => {
                if (_.has(items, id)) {
                    value = _.toString(items[id]);

                    if (
                        (value === '' || (_.has(parameters_options, id) && !_.includes(parameters_options[id], value)))
                        && _.has(parameters_last_options, id)
                    ) {
                        value = _.toString(parameters_last_options[id]);
                    }
                }

                return { ...result, [id]: value };
            }, {});

            this.onChangeSetting('parameters', new_parameters);

            this.showSnackbar('success', __('Parametre boli zaradené podľa dostupných dát o produkte'));
        });
    }

    translateAllegro(market, field) {
        const { allegro_main_market, settings } = this.state;

        let input = settings.markets[allegro_main_market][field];

        if (input === '') {
            this.showSnackbar('error', __('Zdrojový text {MARKET} trhu nie je vyplnený').replace('{MARKET}', allegro_main_market.toUpperCase()));
            return;
        }

        this.setState({ allegro_ai_translate_loading: true });

        request(`/ai-functions/run/${AI_TRANSLATE_FUNCTION_SLUG}`, {
            input,
            item_type: 'manual',
            item_id: null,
            variables: { language: market },
        }, 'POST').then(response => {
            const { status, data } = response.data;

            this.setState({ allegro_ai_translate_loading: false });

            if (status === 'error') {
                let error = data.error;

                if (error === 'Empty credit') {
                    error = __('Nemáte dostatočný kredit');
                }

                this.showSnackbar('error', `${__('Chyba')}: ${error}`);
                return;
            }

            this.onChangeSetting('markets', { ...settings.markets, [market]: { ...settings.markets[market], [field]: data.output } });
        });
    }

    renderAllegroParameters() {
        const {
            allegro_parameters,
            allegro_parameter_translations,
            settings,
            allegro_ai_pair_parameters_loading,
            allegro_ai_pair_parameters_price,
            allegro_ai_pair_parameters_error,
        } = this.state;

        const parameters = _.reduce(allegro_parameters, (result, parameter) => {
            if (this.isRequiredAllegroParameter(parameter, settings)) {
                return [ ...result, parameter ];
            }

            return result;
        }, []);

        if (_.isEmpty(parameters)) {
            return <Message type="info">{__('Kategória nemá žiadne povinné parametre.')}</Message>;
        }

        const showAiError = allegro_ai_pair_parameters_error !== '';

        return (
            <div className="product-marketplace-content__panel__marketplace__parameters__items">
                <Message type="info">{__('Zoznam parametrov ktoré Allegro vyžaduje vyplniť. Niektoré údaje boli automaticky predvyplnené.')}</Message>
                {_.map(parameters, parameter => {
                    if (!_.has(parameter, 'type') || !_.has(parameter, 'name')) {
                        return null;
                    }

                    const label = _.has(allegro_parameter_translations, parameter.name) ? allegro_parameter_translations[parameter.name] : parameter.name;
                    const value = _.has(settings.parameters, parameter.id) ? settings.parameters[parameter.id] : '';

                    switch (parameter.type) {
                        case 'dictionary':
                            let options = _.has(parameter, 'dictionary') ? parameter.dictionary : [];
                            const is_autocomplete = options.length > 100;

                            let selected = null;

                            _.each(options, item => {
                                if (item.id.toString() === value.toString()) {
                                    selected = item;
                                }
                            });

                            if (is_autocomplete) {
                                return <Input
                                    label={label}
                                    content={<Autocomplete
                                        options={options}
                                        getOptionLabel={option => option.value}
                                        onChange={(event, value) => this.onChangeAllegroParameter(parameter.id, value !== null ? value.id : '')}
                                        renderInput={(params) => <TextField
                                            { ...params }
                                            placeholder={__('Začnite písať názov...')}
                                            variant="outlined"
                                        />}
                                        noOptionsText={__('Nenašla sa zhoda')}
                                        clearText={__('Zrušiť')}
                                        value={selected}
                                    />}
                                />;
                            }

                            options = _.reduce(options, (result, option) => {
                                if (!_.has(option, 'id') || !_.has(option, 'value')) {
                                    return result;
                                }

                                const value = _.has(allegro_parameter_translations, option.value)
                                    ? `${allegro_parameter_translations[option.value]} / ${option.value}`
                                    : option.value;

                                return { ...result, [option.id]: value };
                            }, {});

                            return (
                                <Select
                                    label={label}
                                    options={options}
                                    value={value}
                                    onChange={value => this.onChangeAllegroParameter(parameter.id, value)}
                                    allowEmpty={false}
                                />
                            );

                        case 'integer':
                        case 'float':
                        case 'string':
                            let placeholder = [];
                            let min_length = _.has(parameter.restrictions, 'minLength') ? toNumber(parameter.restrictions.minLength) : 0;
                            let max_length = _.has(parameter.restrictions, 'maxLength') ? toNumber(parameter.restrictions.maxLength) : 0;

                            if (min_length > 0) {
                                placeholder = [ ...placeholder, `${__('Minimálna dĺžka')}: ${min_length}` ];
                            }

                            if (max_length > 0) {
                                placeholder = [ ...placeholder, `${__('Maximálna dĺžka')}: ${max_length}` ];
                            }

                            return (
                                <Input
                                    label={label}
                                    value={value}
                                    onChange={value => this.onChangeAllegroParameter(parameter.id, value)}
                                    maxLength={max_length > 0 ? max_length : null}
                                    placeholder={placeholder.join(', ')}
                                />
                            );
                    }

                    return null;
                })}
                <div className="product-marketplace-content__panel__marketplace__parameters__items__buttons">
                    <ButtonBase
                        onClick={() => this.pairAllegroParameters()}
                        className="product-marketplace-content__panel__marketplace__parameters__items__buttons__button-ai"
                    >
                        {allegro_ai_pair_parameters_loading ? this.renderLoading(16) : <SmartToyIcon />}
                        <span>{allegro_ai_pair_parameters_loading ? __('Spracováva sa') : __('Zaradiť chýbajúce parametre cez AI')}
                    </span>
                    </ButtonBase>
                    {showAiError ? <div className="product-marketplace-content__panel__marketplace__parameters__items__buttons__error">{allegro_ai_pair_parameters_error}</div> : null}
                    {!showAiError && allegro_ai_pair_parameters_price !== '' ? <div className="product-marketplace-content__panel__marketplace__parameters__items__buttons__price">{__('Cena')}: {formatAmount(getCurrencyPrice(allegro_ai_pair_parameters_price), getCurrency(), 8)}</div> : null}
                </div>
            </div>
        );
    }

    renderAllegro() {
        const {
            allegro_categories_searched,
            settings,
            loadingAllegroParameters,
            data,
            allegro_market,
            allegro_main_market,
            loadingCreate,
            loadingRemove,
            marketplace_product_id,
            allegro_invalid_category,
            create_error,
            allegro_ai_translate_loading,
        } = this.state;

        if (loadingCreate || loadingRemove) {
            return (
                <div className="product-marketplace-content__panel__marketplace-loading">{this.renderLoading(40)}</div>
            );
        }

        if (marketplace_product_id !== '') {
            return (
                <div className="product-marketplace-content__panel__marketplace">
                    <Message type="success">{__('Produkt bol v Allegro vytvorený.')}</Message>
                    <Button
                        onClick={() => this.confirmRemove(marketplace_product_id)}
                        color="red"
                        className="product-marketplace-content__panel__marketplace__button"
                    >{__('Zmazať z trhoviska')}</Button>
                </div>
            );
        }

        const showCategory = settings.product_id === '';
        const showParameters = settings.category_id !== '';
        const isSelectedCategory = !showCategory || settings.category_id !== '';

        const allowSave = isSelectedCategory && !loadingAllegroParameters;
        const isMainMarket = allegro_market === allegro_main_market;

        return (
            <div className="product-marketplace-content__panel__marketplace">
                {showCategory ? <Input
                    label={__('Allegro kategória')}
                    content={<Autocomplete
                        options={allegro_categories_searched}
                        getOptionLabel={option => option.name}
                        onChange={(event, value) => this.selectAllegroCategory(value.id)}
                        renderInput={(params) => <TextField
                            { ...params }
                            placeholder={__('Začnite písať názov produktu/kategórie (okuliare, televizor)...')}
                            variant="outlined"
                            onChange={event => this.allegroSearchCategories(event.target.value)}
                        />}
                        noOptionsText={__('Nenašla sa žiadna kategória')}
                        clearText={__('Zrušiť')}
                        freeSolo
                    />}
                /> : <Message type="warning">{__('Produkt s rovnakým EAN už je evidovaný v Allegro, kategória sa nevyplňuje.')}</Message>}
                {!isSelectedCategory && !allegro_invalid_category ? <Message type="info" style={{ marginTop: '20px' }}>{__('Najprv musíte vybrať Allegro kategóriu.')}</Message> : null}
                {!isSelectedCategory && allegro_invalid_category ? <Message type="warning" style={{ marginTop: '20px' }}>{__('Allegro kategória neumožnuje vytváranie produktov. Prosím vyberte inú.')}</Message> : null}
                {showParameters ? <div className="product-marketplace-content__panel__marketplace__parameters">
                    <div className="input__label">{__('Allegro parametre')}</div>
                    {loadingAllegroParameters ? this.renderLoading(20) : this.renderAllegroParameters()}
                </div> : null}
                {isSelectedCategory ? <Tabs
                    value={allegro_market}
                    onChange={(event, tab) => this.onChangeAllegroMarket(tab)}
                    className="product-marketplace-content__panel__marketplace__tabs"
                >
                    {_.map(data.marketplace.data.markets, market => <Tab label={market.toUpperCase()} value={market} key={market} />)}
                </Tabs> : null}
                {isSelectedCategory ? <div className="product-marketplace-content__panel__marketplace__inputs">
                    {!isMainMarket ? <Checkbox
                        label={__('Aktivovať')}
                        value={settings.markets[allegro_market].active}
                        onChange={checked => this.onChangeAllegroMarketSetting('active', checked)}
                    /> : null}
                    <Input
                        label={__('Názov')}
                        value={settings.markets[allegro_market].name}
                        onChange={value => this.onChangeAllegroMarketSetting('name', value)}
                        disabled={!settings.markets[allegro_market].active}
                    />
                    {!isMainMarket && settings.markets[allegro_market].active ? <div className="product-marketplace-content__panel__marketplace__inputs__buttons">
                        <ButtonBase
                            onClick={() => this.translateAllegro(allegro_market, 'name')}
                            className="product-marketplace-content__panel__marketplace__inputs__buttons__button-ai"
                        >
                            {allegro_ai_translate_loading ? this.renderLoading(16) : <SmartToyIcon />}
                            <span>{allegro_ai_translate_loading ? __('Spracováva sa') : __('Preložiť cez AI')}</span>
                        </ButtonBase>
                    </div> : null}
                    {settings.markets[allegro_market].active ? <div className="input__label editor-label">{__('Popis')}</div> : null}
                    {settings.markets[allegro_market].active ? <CKEditor
                        editor={Editor}
                        data={settings.markets[allegro_market].description}
                        onChange={(event, editor) => this.onChangeAllegroMarketSetting('description', editor.getData())}
                    /> : null}
                    {!isMainMarket && settings.markets[allegro_market].active ? <div className="product-marketplace-content__panel__marketplace__inputs__buttons">
                        <ButtonBase
                            onClick={() => this.translateAllegro(allegro_market, 'description')}
                            className="product-marketplace-content__panel__marketplace__inputs__buttons__button-ai"
                        >
                            {allegro_ai_translate_loading ? this.renderLoading(16) : <SmartToyIcon />}
                            <span>{allegro_ai_translate_loading ? __('Spracováva sa') : __('Preložiť cez AI')}</span>
                        </ButtonBase>
                    </div> : null}
                    <Input
                        label={`${__('Cena')} (${settings.markets[allegro_market].currency})`}
                        value={settings.markets[allegro_market].price}
                        onChange={value => this.onChangeAllegroMarketSetting('price', value)}
                        disabled={!settings.markets[allegro_market].active}
                    />
                    {isMainMarket ? <Input
                        label={__('Stav na sklade')}
                        value={settings.stock}
                        onChange={value => this.onChangeSetting('stock', value)}
                    /> : null}
                </div> : null}
                {create_error !== '' ? <Message type="error" style={{ marginTop: '20px' }}>{create_error}</Message> : null}
                <Button
                    onClick={() => this.create('allegro')}
                    color="green"
                    className="product-marketplace-content__panel__marketplace__button"
                    disabled={!allowSave}
                >{__('Pridať na trhovisko')}</Button>
            </div>
        );
    }

    renderMarketplace() {
        const { data, marketplace, loadingMarketplace, marketplace_id } = this.state;

        if (loadingMarketplace) {
            return (
                <div className="product-marketplace-content__panel__marketplace-loading">{this.renderLoading(40)}</div>
            );
        }

        if (_.isEmpty(marketplace)) {
            return (
                <div className="product-marketplace-content__panel__marketplace">
                    <Message type="warning">{__('Trhovisko nie je aktivované. Aktivujete ho v sekcii Trhoviská.')}</Message>
                </div>
            );
        }

        switch (data.marketplaces[marketplace_id]) {
            case 'Allegro':
                return this.renderAllegro();

            default:
                return null;
        }
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { data, marketplace_id } = this.state;

        if (_.isEmpty(data)) {
            return <div
                className="product-marketplace-content product-marketplace-content-loading"
            >{this.renderLoading()}</div>;
        }

        return (
            <div className="product-marketplace-content">
                <div className="product-marketplace-content__panel">
                    <div className="product-marketplace-content__panel__photo">
                        {!_.isEmpty(data.product.img_url) ? <img src={data.product.img_url} /> : null}
                    </div>
                    <div className="product-marketplace-content__panel__photos">
                        {_.map(data.product.images, image => {
                            if (image === data.product.img_url) {
                                return null;
                            }

                            return (
                                <div className="product-marketplace-content__panel__photos__photo" key={image}>
                                    <img src={image} />
                                </div>
                            );
                        })}
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('Stav')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{data.product.active
                            ? <span className="product-marketplace-content__panel__value__text-active">{__('Aktívny')}</span>
                            : <span className="product-marketplace-content__panel__value__text-inactive">{__('Neaktívny')}</span>
                        }</div>
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('Číslo')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{this.renderValue(data.product.number)}</div>
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('EAN')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{this.renderValue(data.product.ean)}</div>
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('Stav na sklade')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{data.product.stock} {__('ks')}</div>
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('Váha')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{formatAmount(data.product.weight, '')} {__('kg')}</div>
                    </div>
                    <div className="product-marketplace-content__panel__value">
                        <div className="product-marketplace-content__panel__value__label">{__('Výrobca')}:</div>
                        <div className="product-marketplace-content__panel__value__text">{this.renderValue(data.product.manufacturer)}</div>
                    </div>
                    {!_.isEmpty(data.product.categories) ? <div className="product-marketplace-content__panel__label">{__('Kategórie')}</div> : null}
                    {!_.isEmpty(data.product.categories) ? <div className="product-marketplace-content__panel__tags">
                        {_.map(data.product.categories, category_id => {
                            if (_.has(data.categories, category_id)) {
                                return <Chip
                                    label={data.categories[category_id]}
                                    key={category_id}
                                    style={{ marginRight: '2px', marginTop: '2px' }}
                                />;
                            }

                            return null;
                        })}
                    </div> : null}
                </div>
                <div className="product-marketplace-content__panel">
                    <div className="product-marketplace-content__panel__marketplaces">
                        {_.map(data.marketplaces, (name, id) => {
                            if (name === 'Heureka') {
                                return null;
                            }

                            id = toNumber(id);

                            const activated = _.includes(_.values(data.user_marketplaces), id);

                            return (
                                <Tooltip title={MARKETPLACES[name]} key={id}>
                                    <ButtonBase
                                        onClick={() => this.changeMarketplace(id)}
                                        style={{ backgroundColor: MARKETPLACES_COLORS[name].background }}
                                        className={id === marketplace_id ? 'active' :  null}
                                    >
                                        <img src={IMAGES[`${name.toLowerCase()}.png`]} alt={name} />
                                        {activated
                                            ? <ActivatedIcon color="secondary" />
                                            : null
                                        }
                                    </ButtonBase>
                                </Tooltip>
                            );
                        })}
                    </div>
                    {this.renderMarketplace()}
                </div>
                {this.renderLightbox(
                    'remove',
                    __('Naozaj chcete zmazať tento produkt z trhoviska?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.remove()
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { ProductMarketplace };
